import { apiClient } from '@/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import Alert from '@/components/Alert';
import AuthLayout from '@/components/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Translator, { useTranslation } from 'components/i18n/Translator';

import { useAuth } from 'client-types/*';

const schema = z.object({
  email: z.string().email()
});

type FormValues = z.infer<typeof schema>;

export default function ForgotPassword() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' }
  });

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    try {
      await apiClient.forgotPassword(data.email);
    } catch {
      // generic confirmation regardless of outcome — no enumeration leak
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <AuthLayout>
      {submitted ? (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-2xl font-bold">
              <Translator path="auth.forgot.confirmation.title" />
            </h1>
          </div>
          <Alert variant="info">
            <span aria-live="polite">
              {t('auth.forgot.confirmation.message')}
            </span>
          </Alert>
          <Link
            to="/login"
            className="text-center text-sm text-primary underline-offset-4 hover:underline"
          >
            <Translator path="auth.forgot.actions.backToLogin" />
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-2xl font-bold">
              <Translator path="auth.forgot.title" />
            </h1>
            <p className="text-sm text-muted-foreground">
              <Translator path="auth.forgot.subtitle" />
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">
              <Translator path="auth.forgot.form.email.label" />
            </Label>
            <Input
              id="email"
              type="email"
              autoFocus
              disabled={loading}
              placeholder={t('auth.forgot.form.email.placeholder')}
              {...register('email')}
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && (
              <p className="text-sm text-destructive">
                {t('auth.forgot.form.email.invalid')}
              </p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            <Translator path="auth.forgot.actions.submit" />
          </Button>

          <Link
            to="/login"
            className="text-center text-sm text-primary underline-offset-4 hover:underline"
          >
            <Translator path="auth.forgot.actions.backToLogin" />
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}
